import { SUPPORTED_FILE_TYPES, FileExtension, Signature } from './fileTypes';
import { FileValidationMessages } from './fileValidationMessages';

/**
 * Single file checks. Each one throws an Error with the message of `FileValidationMessages` when
 * the file does not pass it.
 *
 * `extension`, `singleExtension` and `size` only need the name and size of the file, so they also
 * work in the browser with a `File`. `mimeType` and `content` need the uploaded data (server only).
 */
export class FileChecks {
  /** Only allows the given extensions. Returns the extension of the file. */
  static extension(file: { name: string }, validExtensions: FileExtension[]): FileExtension {
    const fileExtension = FileChecks.supportedExtensionOf(file.name);

    if (!fileExtension || !validExtensions.includes(fileExtension)) {
      throw new Error(FileValidationMessages.types(validExtensions));
    }

    return fileExtension;
  }

  /**
   * Rejects file names with more than one extension (e.g. `logo.php.png`). A known compound
   * extension (`backup.tar.gz`) counts as one, even though it has more than one dot.
   */
  static singleExtension(file: { name: string }) {
    const fileExtension = FileChecks.supportedExtensionOf(file.name);
    // When the extension is unknown, only the last `.xyz` part counts as the extension
    const extensionDots = fileExtension?.split('.').length ?? 1;
    const nameDots = file.name.split('.').length - 1;

    if (nameDots > extensionDots) {
      throw new Error(FileValidationMessages.multipleExtensions);
    }
  }

  /** Limits the size of the file, in bytes. */
  static size(file: { size: number }, maxBytes: number) {
    if (file.size > maxBytes) {
      throw new Error(FileValidationMessages.size(maxBytes));
    }
  }

  /** Requires the mime type sent by the client to be one of the type of the extension. */
  static mimeType(file: { mimetype: string }, extension: FileExtension) {
    if (!SUPPORTED_FILE_TYPES[extension].mimeTypes.includes(file.mimetype)) {
      throw new Error(FileValidationMessages.mimeTypeMismatch);
    }
  }

  /** Requires the actual content of the file to match any of the signatures of the extension. */
  static content(file: { data: Buffer }, extension: FileExtension) {
    if (!FileChecks.matchesSignatures(file.data, SUPPORTED_FILE_TYPES[extension].signatures)) {
      throw new Error(FileValidationMessages.contentMismatch);
    }
  }

  /**
   * Extension of the file name, without the dot. When several catalog extensions are
   * suffixes of the name, the longest wins (`backup.tar.gz` => `tar.gz`, not `gz`).
   * Undefined when the name ends in no supported extension.
   */
  private static supportedExtensionOf(fileName: string): FileExtension | undefined {
    const name = fileName.toLowerCase();

    const supportedExtensions = Object.keys(SUPPORTED_FILE_TYPES) as FileExtension[];

    return supportedExtensions
      .sort((a, b) => b.length - a.length)
      .find(extension => name.endsWith(`.${extension}`));
  }

  // Types without signatures (e.g. CSV) skip the content check
  private static matchesSignatures(data: Buffer, signatures: Signature[]): boolean {
    if (signatures.length === 0) return true;

    return signatures.some(signature =>
      signature.every(({ offset, bytes }) =>
        bytes.every((byte, index) => data[offset + index] === byte)
      )
    );
  }
}
